/** @module Camunda/TaskDefinitionForm */
define([
	"dojo/_base/declare",
	"csi",
	"csi/def",
	"csi/def/ViewStoreBuilder",
	"csi/form/Widget",
	"csi/heading/_base",
	"csi/html",
	"csi/locale",
	"csi/prefs",
	"csi/reflection",
	"csi/state",
	"csi/state/rootStore",
	"csi/widget/GridSelect",
	"csi/xml",
	"Camunda",
	"./TaskActionGroup",
	"./_TaskSetupMixin"
], function(declare, csi, def, ViewStoreBuilder, Widget, csiHeading, html, locale, csiPrefs, reflection, state, {sessionStore}, GridSelect, xml, Camunda, TaskActionGroup, _TaskSetupMixin) {

	const ATTR_CUSTOM_VIEW = "CustomView";
	const ATTR_PORTAL = "ViewPortal";
	const ATTR_VIEW = "ViewStoreId";
	const ATTR_VIEW_PATH = "ViewDefinitionPath";
	const PORTAL_HOST = "Host";
	const PORTAL_SUPPLIER = "Supplier";

	const _supplierPortalTypeStore = def.getPortalTypeStore(def.getHostViewId(PORTAL_SUPPLIER), function(rc) {
		if (rc.isRevision()) {
			// revision class has portal root view, but master class is valid for Camunda
			rc = rc.getMasterClass();
		}

		return Camunda.isValidClass(rc);
	});

	return declare([Widget, _TaskSetupMixin], {
		bSkipFocusFirstField: true,

		/** @property {string} primaryType - primary type for task */
		primaryType: "",

		/** @property {string} primarySubtype - primary subtype URL for task, if any */
		primarySubtype: "",

		/** @property {boolean} _supportsCustomViews - set to true if selected view supports custom views */
		_supportsCustomViews: false,

		/**
		 * @override
		 * Return custom store for classes and custom view
		 */
		getFieldStore: function(field, value) {
			const name = field.getControlName();
			var store;

			switch (name) {
				case ATTR_CUSTOM_VIEW:
					store = this._getCustomViewStore(value);
					break;

				case ATTR_VIEW:
					store = this._getViewStore();
					break;
			}

			return store || this.inherited(arguments);
		},

		/**
		 * @override
		 * Add ViewDefinitionPath based on chosen view
		 */
		getFormNode: function() {
			const node = this.inherited(arguments);
			const viewItem = this._getViewItem();

			if (viewItem) {
				xml.nodeCreateClientAttr(node, ATTR_VIEW_PATH, xml.ATTR_STRING, viewItem.extendedPath.toString());
			}

			return node;
		},

		/**
		 * @override
		 * Read value for field "Info" aka "Description". Allow to edit string corresponding to current site locale
		 */
		getValueForField: function(field, node) {
			if (field.m_name == "Info") {
				var localeName = state.get("siteLocale");
				var node = this.getContext().getNode();
				var info = node.Info;
				return info[localeName] || "";
			} else {
				return this.inherited(arguments);
			}
		},

		/**
		 * Return extended path to selected view as a string
		 * @return {string}
		 */
		getViewDefinitionPath: function() {
			const item = this._getViewItem();
			return item?.extendedPath?.toString() ?? "";
		},

		/**
		 * Reset view field when portal changes
		 * @param {FormView} defView
		 * @param {Field} field
		 * @param {string} value
		 */
		onChangePortal: function(defView, field, value) {
			this.resetFieldStoreAndValue(ATTR_VIEW, "");
		},

		/**
		 * Reset custom view field when view changes
		 * @param {FormView} defView
		 * @param {Field} field
		 * @param {string} value
		 */
		onChangeView: function(defView, field, value) {
			this.resetFieldStoreAndValue(ATTR_CUSTOM_VIEW, csi.EMPTY_URL);
		},

		/**
		 * Use TaskActionGroup widget to render and edit actionGroup and actions settings
		 * @param {FormView} defView
		 * @param {Field} field
		 * @param {string} value
		 * @param {DOMNode} div
		 * @param {Widget} [control]
		 * @param {Object} [widgetParams]
		 */
		onDrawActionGroup: function(defView, field, value, div, control, widgetParams) {
			widgetParams.view = this;
			field.m_control = new TaskActionGroup(widgetParams).placeAt(div);
			this.resetFieldStoreAndValue(field, value);
		},

		/**
		 * Render action to show example view
		 * @param {FormView} defView
		 * @param {Field} field
		 * @param {string} value
		 * @param {DOMNode} div
		 * @param {Widget} [control]
		 * @param {Object} [widgetParams]
		 */
		onDrawCustomView: function(defView, field, value, div, control, widgetParams) {
			this._renderTaskSetupExampleAction(this.getFormContext(), div);
		},

		/**
		 * Render view selection
		 * @param {FormView} defView
		 * @param {Field} field
		 * @param {string} value
		 * @param {DOMNode} div
		 * @param {Widget} [control]
		 * @param {Object} [widgetParams]
		 */
		onDrawView: function(defView, field, value, div, control, widgetParams) {
			const self = this;

			field.m_control = new GridSelect({
				expandFirstLevel: true,
				isTree: true,
				required: true,

				onChange: function(value) {
					self.onChangeField(field);
				}
			}).placeAt(div);

			this.resetFieldStoreAndValue(field, value);
		},

		/**
		 * Render Task Name, Type and Task Role
		 * @param {FormView} defView
		 * @param {Field} field
		 * @param {string} value
		 * @param {DOMNode} div
		 * @param {Widget} [control]
		 */
		onDrawReadOnlyField: function(defView, field, value, div, control) {
			csiHeading.toHtml(this.getContext(), field.heading, div);
		},

		/**
		 * @override
		 * Determine primary type and subtype
		 */
		onViewCreate: function() {
			const task = this.getViewNode();
			const processRevision = this.cacheView.get(task.__Parent__);
			const process = this.cacheView.get(processRevision.__Parent__);
			this.primaryType = process.PrimaryType;
			this.primarySubtype = process.PrimarySubtype;
			this._addTaskSetupExampleAction();
			this._addTaskSetupTitle();
			state.set("modalView", this);
		},

		/**
		 * @override
		 * Set portal as read-only if primary type is not available in Supplier portal
		 */
		onViewStart: function(dfds) {
			var rc = reflection.getClass(this.primaryType);

			if (rc.isMaster()) {
				rc = rc.getRevisionClass();
			}

			if (!_supplierPortalTypeStore.get(rc.getType())) {
				this.setFieldDisabled(ATTR_PORTAL, true);
			}
		},

		/**
		 * @override
		 * Reset modal view state
		 */
		uninitialize: function() {
			state.set("modalView", null);
			this.inherited(arguments);
		},

		/**
		 * Return data store for selecting custom view
		 * @param {string} value
		 * @return {Names|null}
		 */
		_getCustomViewStore: function(value) {
			const item = this._getViewItem();
			const key = item && ViewStoreBuilder.getCustomViewContainerKey(item.id, item.extendedPath, this.primarySubtype);
			if (!key) {
				this._supportsCustomViews = false;
				return null;
			}

			this._supportsCustomViews = true;

			// create custom view store
			const ctx = this.getContext();
			const task = ctx.getNode();
			const customView = ctx.cache.get(value);
			const roleName = task.TaskRole;
			var urlRole = null;
			sessionStore.playableRolesStore.internalQuery({Name: roleName}).forEach(function(item) {
				urlRole = item.$;
			});
			return csiPrefs.getCustomViewNamesStore(key, urlRole, null, customView);
		},

		/**
		 * Return selected item from view store
		 * @return {ViewStoreItem|null}
		 */
		_getViewItem: function() {
			const w = this.getFieldControl(ATTR_VIEW);
			return w ? w.getSelection() : null;
		},

		/**
		 * Return data store to use for view selection widget
		 * @return {Memory|null}
		 */
		_getViewStore: function() {
			const portal = xml.enumGetText(this.getFieldValue(ATTR_PORTAL));
			const rootViewId = def.getHostViewId(portal);
			const store = ViewStoreBuilder.getDStore(this.primaryType, rootViewId, {
				filter: function(item) {
					return item.extendedPath && item.visible;
				}
			});

			return store;
		},

		/**
		 * @override
		 * Set visibility of custom view field based on if selected view supports custom views
		 */
		_updateFieldVisibility: function(field) {
			this.inherited(arguments);
			this.setFieldDisabled(ATTR_CUSTOM_VIEW, !this._supportsCustomViews);
			this.setFieldRequired(ATTR_CUSTOM_VIEW, this._supportsCustomViews);
			$(this.domNode).toggleClass("csi-camunda-task-no-example", !this._canSeeExampleView(this.getFormContext()));
			return true;
		},

		/**
		 * @override
		 * Validation should never fail for custom view field, as there is always a default view
		 */
		_validateField: function(field, bSubset) {
			return (field.m_id === ATTR_CUSTOM_VIEW) ? null : this.inherited(arguments);
		}
	});
});
